import type { TFunction } from 'i18next';

export const GAME_WALLET_ERROR_CODES = {
  AMBIGUOUS_RETRY_PENDING: 'GAME_WALLET_AMBIGUOUS_RETRY_PENDING',
  AUTO_REFUND_WARNING: 'PLAYER_AUTO_REFUND_WARNING',
  DEPOSIT_IN_PROGRESS: 'DEPOSIT_IN_PROGRESS',
  PAYOUT_BLOCKED: 'GAME_WALLET_PAYOUT_BLOCKED',
  RECOVERY_PENDING: 'GAME_WALLET_RECOVERY_PENDING',
  PROVIDER_UNHEALTHY: 'GAME_PROVIDER_UNHEALTHY',
  AGENT_INSUFFICIENT_BALANCE: 'AGENT_INSUFFICIENT_BALANCE',
} as const;

export type GameWalletErrorCode =
  (typeof GAME_WALLET_ERROR_CODES)[keyof typeof GAME_WALLET_ERROR_CODES];

const GAME_WALLET_ERROR_I18N_KEYS: Record<GameWalletErrorCode, string> = {
  [GAME_WALLET_ERROR_CODES.AMBIGUOUS_RETRY_PENDING]:
    'finance.gameWalletErrors.ambiguousRetryPending',
  [GAME_WALLET_ERROR_CODES.AUTO_REFUND_WARNING]:
    'finance.gameWalletErrors.autoRefundWarning',
  [GAME_WALLET_ERROR_CODES.DEPOSIT_IN_PROGRESS]:
    'finance.gameWalletErrors.depositInProgress',
  [GAME_WALLET_ERROR_CODES.RECOVERY_PENDING]:
    'finance.gameWalletErrors.recoveryPending',
  [GAME_WALLET_ERROR_CODES.PAYOUT_BLOCKED]:
    'finance.gameWalletErrors.payoutBlocked',
  [GAME_WALLET_ERROR_CODES.PROVIDER_UNHEALTHY]:
    'finance.gameWalletErrors.providerUnhealthy',
  [GAME_WALLET_ERROR_CODES.AGENT_INSUFFICIENT_BALANCE]:
    'finance.gameWalletErrors.agentInsufficientBalance',
};

const GAME_WALLET_ERROR_FALLBACKS: Record<GameWalletErrorCode, string> = {
  [GAME_WALLET_ERROR_CODES.AMBIGUOUS_RETRY_PENDING]:
    'We could not confirm your deposit to this game. We are resolving it automatically. Please wait a moment or try another game.',
  [GAME_WALLET_ERROR_CODES.AUTO_REFUND_WARNING]:
    'Withdrawals are temporarily unavailable while your account is under review due to recent deposit refunds. Please contact support.',
  [GAME_WALLET_ERROR_CODES.DEPOSIT_IN_PROGRESS]:
    'A deposit is already being processed for this game. Please wait until it completes.',
  [GAME_WALLET_ERROR_CODES.RECOVERY_PENDING]:
    'This game is temporarily unavailable because a withdrawal is still in progress. Please try another game or wait until it completes.',
  [GAME_WALLET_ERROR_CODES.PAYOUT_BLOCKED]:
    'A game wallet withdrawal is still in progress. You may continue playing other games. Please contact support if this persists.',
  [GAME_WALLET_ERROR_CODES.PROVIDER_UNHEALTHY]:
    'This game is temporarily unavailable. Please try again later or choose another game.',
  [GAME_WALLET_ERROR_CODES.AGENT_INSUFFICIENT_BALANCE]:
    'This game is currently unavailable for deposits. Please contact an admin for support.',
};

export type GameWalletFlow = 'deposit' | 'withdraw' | 'transaction';

type ProviderUnhealthyCopy = {
  singleKey: string;
  multipleKey: string;
  buildSingle: (providerName: string) => string;
  buildMultiple: (providerNamesText: string) => string;
};

const PROVIDER_UNHEALTHY_COPY: Record<GameWalletFlow, ProviderUnhealthyCopy> = {
  deposit: {
    singleKey: 'finance.gameWalletErrors.providerUnhealthyDeposit',
    multipleKey: 'finance.gameWalletErrors.providerUnhealthyDepositMultiple',
    buildSingle: (providerName) =>
      `${providerName} is temporarily unavailable. Please deposit to other games and try again later.`,
    buildMultiple: (providerNamesText) =>
      `${providerNamesText} are temporarily unavailable. Please deposit to other games and try again later.`,
  },
  withdraw: {
    singleKey: 'finance.gameWalletErrors.providerUnhealthyWithdraw',
    multipleKey: 'finance.gameWalletErrors.providerUnhealthyWithdrawMultiple',
    buildSingle: (providerName) =>
      `${providerName} is temporarily unavailable. Please redeem from other games and try again later.`,
    buildMultiple: (providerNamesText) =>
      `${providerNamesText} are temporarily unavailable. Please redeem from other games and try again later.`,
  },
  transaction: {
    singleKey: 'finance.gameWalletErrors.providerUnhealthy',
    multipleKey: 'finance.gameWalletErrors.providerUnhealthyMultiple',
    buildSingle: (providerName) =>
      `${providerName} is temporarily unavailable. Please try again later or choose another game.`,
    buildMultiple: (providerNamesText) =>
      `${providerNamesText} are temporarily unavailable. Please try again later or choose another game.`,
  },
};

type ProviderDetails = {
  providerName?: string;
  providerCode?: string;
};

function readCodeFromObject(value: Record<string, unknown>): string | undefined {
  if (typeof value.code === 'string') {
    return value.code;
  }
  if (value.data && typeof value.data === 'object') {
    const nestedCode = (value.data as Record<string, unknown>).code;
    if (typeof nestedCode === 'string') {
      return nestedCode;
    }
  }
  return undefined;
}

function readDetailsFromObject(value: Record<string, unknown>): Record<string, unknown> | undefined {
  if (value.details && typeof value.details === 'object') {
    return value.details as Record<string, unknown>;
  }
  if (value.data && typeof value.data === 'object') {
    const nestedDetails = (value.data as Record<string, unknown>).details;
    if (nestedDetails && typeof nestedDetails === 'object') {
      return nestedDetails as Record<string, unknown>;
    }
  }
  return undefined;
}

function extractProviderNames(details?: Record<string, unknown>): string[] {
  if (!details) {
    return [];
  }

  const unhealthyProviders = details.unhealthyProviders;
  if (Array.isArray(unhealthyProviders)) {
    return unhealthyProviders
      .map((provider) => {
        if (!provider || typeof provider !== 'object') {
          return undefined;
        }
        const typedProvider = provider as ProviderDetails;
        return typedProvider.providerName || typedProvider.providerCode;
      })
      .filter((name): name is string => Boolean(name));
  }

  const providerName = details.providerName;
  if (typeof providerName === 'string' && providerName.trim()) {
    return [providerName.trim()];
  }

  const providerCode = details.providerCode;
  if (typeof providerCode === 'string' && providerCode.trim()) {
    return [providerCode.trim()];
  }

  return [];
}

function resolveProviderNamedMessage(
  error: unknown,
  t: TFunction | undefined,
  i18nKey: string,
  buildFallback: (providerName: string) => string,
): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorObject = error as Record<string, unknown>;
  const details = readDetailsFromObject(errorObject);
  const providerNames = extractProviderNames(details);

  if (providerNames.length >= 1) {
    const providerName = providerNames[0];
    const fallback = buildFallback(providerName);
    return t
      ? t(i18nKey, {
          providerName,
          defaultValue: fallback,
        })
      : fallback;
  }

  if (typeof errorObject.message === 'string' && errorObject.message.trim()) {
    return errorObject.message.trim();
  }

  return undefined;
}

function resolveProviderUnhealthyMessage(
  error: unknown,
  t?: TFunction,
  flow: GameWalletFlow = 'transaction',
): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorObject = error as Record<string, unknown>;
  if (typeof errorObject.message === 'string' && errorObject.message.trim()) {
    return errorObject.message.trim();
  }

  const copy = PROVIDER_UNHEALTHY_COPY[flow];
  const details = readDetailsFromObject(errorObject);
  const providerNames = extractProviderNames(details);

  if (providerNames.length > 1) {
    const providerNamesText = providerNames.join(', ');
    const fallback = copy.buildMultiple(providerNamesText);
    return t
      ? t(copy.multipleKey, {
          providerNames: providerNamesText,
          defaultValue: fallback,
        })
      : fallback;
  }

  return resolveProviderNamedMessage(
    error,
    t,
    copy.singleKey,
    copy.buildSingle,
  );
}

function resolveAgentInsufficientBalanceMessage(
  error: unknown,
  t?: TFunction,
): string | undefined {
  return resolveProviderNamedMessage(
    error,
    t,
    'finance.gameWalletErrors.agentInsufficientBalance',
    (providerName) =>
      `${providerName} is currently unavailable for deposits. Please contact an admin for support.`,
  );
}

function resolveRecoveryPendingMessage(
  error: unknown,
  t?: TFunction,
): string | undefined {
  return resolveProviderNamedMessage(
    error,
    t,
    'finance.gameWalletErrors.recoveryPending',
    (providerName) =>
      `${providerName} is temporarily unavailable because a withdrawal is still in progress. Please try another game or wait until it completes.`,
  );
}

function resolveAmbiguousRetryPendingMessage(
  error: unknown,
  t?: TFunction,
): string | undefined {
  return resolveProviderNamedMessage(
    error,
    t,
    'finance.gameWalletErrors.ambiguousRetryPending',
    (providerName) =>
      `We could not confirm your deposit to ${providerName}. We are resolving it automatically. Please wait a moment or try another game.`,
  );
}

function resolveDepositInProgressMessage(
  error: unknown,
  t?: TFunction,
): string | undefined {
  return resolveProviderNamedMessage(
    error,
    t,
    'finance.gameWalletErrors.depositInProgress',
    (providerName) =>
      `A deposit is already being processed for ${providerName}. Please wait until it completes.`,
  );
}

function resolvePayoutBlockedMessage(
  error: unknown,
  t?: TFunction,
): string | undefined {
  return resolveProviderNamedMessage(
    error,
    t,
    'finance.gameWalletErrors.payoutBlocked',
    (providerName) =>
      `${providerName}: A game wallet withdrawal is still in progress. You may continue playing other games. Please contact support if this persists.`,
  );
}

export function extractGameWalletErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  return readCodeFromObject(error as Record<string, unknown>);
}

export function isGameWalletConflictError(error: unknown): boolean {
  const code = extractGameWalletErrorCode(error);
  return code != null && Object.values(GAME_WALLET_ERROR_CODES).includes(code as GameWalletErrorCode);
}

export function resolveGameWalletErrorMessage(
  error: unknown,
  t?: TFunction,
  fallbackMessage?: string,
  flow: GameWalletFlow = 'transaction',
): string {
  const code = extractGameWalletErrorCode(error);
  if (code && code in GAME_WALLET_ERROR_I18N_KEYS) {
    const typedCode = code as GameWalletErrorCode;

    const providerSpecificResolvers: Partial<
      Record<GameWalletErrorCode, (error: unknown, t?: TFunction, flow?: GameWalletFlow) => string | undefined>
    > = {
      [GAME_WALLET_ERROR_CODES.PROVIDER_UNHEALTHY]: resolveProviderUnhealthyMessage,
      [GAME_WALLET_ERROR_CODES.AGENT_INSUFFICIENT_BALANCE]: resolveAgentInsufficientBalanceMessage,
      [GAME_WALLET_ERROR_CODES.RECOVERY_PENDING]: resolveRecoveryPendingMessage,
      [GAME_WALLET_ERROR_CODES.AMBIGUOUS_RETRY_PENDING]: resolveAmbiguousRetryPendingMessage,
      [GAME_WALLET_ERROR_CODES.DEPOSIT_IN_PROGRESS]: resolveDepositInProgressMessage,
      [GAME_WALLET_ERROR_CODES.PAYOUT_BLOCKED]: resolvePayoutBlockedMessage,
    };

    const providerMessage = providerSpecificResolvers[typedCode]?.(error, t, flow);
    if (providerMessage) {
      return providerMessage;
    }

    const key = GAME_WALLET_ERROR_I18N_KEYS[typedCode];
    const fallback = GAME_WALLET_ERROR_FALLBACKS[typedCode];
    return t ? t(key, fallback) : fallback;
  }

  if (fallbackMessage) {
    return fallbackMessage;
  }

  if (error && typeof error === 'object' && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }

  return t
    ? t('finance.unexpectedErrorTryAgain')
    : 'An unexpected error occurred. Please try again.';
}
